import { Injectable, inject } from '@angular/core';
import {
  ContentfulContentType,
  ContentfulContextualHelp,
  ContentFulEntryDocument,
  ContentFulEntryFaq,
  ContentFulEntryVideo,
  ContentfulUrlAnchor,
  ContentTypes,
} from '@clux-models';
import { documentToHtmlString } from '@contentful/rich-text-html-renderer';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import { Document as ContentfulDocument } from '@contentful/rich-text-types';
import { createClient, EntryCollection } from 'contentful';
import 'regenerator-runtime/runtime';
import { from, Observable, zip } from 'rxjs';
import { map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { BrandService } from './brand.service';
import { BrowserService } from './browser.service';

export type MergedContent = [EntryCollection<ContentFulEntryVideo> | undefined, EntryCollection<ContentFulEntryDocument> | undefined];

@Injectable({
  providedIn: 'root',
})
export class ContentfulService {
  private brandService = inject(BrandService);
  private readonly browserService = inject(BrowserService);

  public client = createClient({
    space: environment.contentful.space,
    accessToken: environment.contentful.accessToken,
    environment: environment.contentful.environment,
  });
  private readonly MAX_NUMBER_OF_LEVELS = 10;
  private readonly brand = this.brandService.getBrandResources();

  /**
   * Get the FAQs for the current brand.
   * @returns The collection of FAQs for the current brand.
   */
  public async getFaqs(): Promise<EntryCollection<ContentFulEntryFaq>> {
    return this.client.getEntries({ content_type: this.brand.faqContentType, select: 'fields' });
  }

  /**
   * Gets the user help materials for the current url path and specified url anchor.
   * @param anchor The optional contentful url anchor for which to retrieve help materials if the path is the same.
   * @returns The help materials for the current url path/specified url anchor.
   */
  public getContextualHelpForCurrentUrl(anchor?: ContentfulUrlAnchor, convertToHtmlString: boolean = true): Observable<ContentfulContextualHelp | undefined> {
    return from(
      this.client.getEntries<ContentfulContextualHelp>({
        content_type: ContentfulContentType.ContextualHelp,
        'fields.url': `${this.browserService.getLocationPathname()}${anchor ? `#${anchor}` : ''}`,
        'fields.brand.fields.title': this.brand.companyName,
        'fields.brand.sys.contentType.sys.id': 'brand',
        include: this.MAX_NUMBER_OF_LEVELS,
      })
    ).pipe(
      map(
        (result) =>
          result?.items[0]?.fields && {
            ...result?.items[0]?.fields,
            description: convertToHtmlString ? documentToHtmlString(result?.items[0]?.fields?.description as ContentfulDocument) : documentToPlainTextString(result?.items[0]?.fields?.description as ContentfulDocument),
          }
      )
    );
  }

  /**
   * Gets only the description field of the user help materials for the current url path and specified url anchor.
   * Useful as a helper function for getContextualHelpForCurrentUrl. If you only need the description content of the ContextualHelp materials,
   * this function will perform extra checks necessary and return the description contents as a string, or an empty string if the content is not a string.
   * @param anchor The optional contentful url anchor for which to retrieve help materials if the path is the same.
   * @param convertToHtmlString Whether to return the text as an html string (true) or plain text string (false)
   * @param defaultDescription Optional text to return if ContextualHelp description does not exist or is not a string
   * @returns The help material description field for the current url path/specified url anchor
   */
  public getContextualHelpDescriptionForUrl(anchor?: ContentfulUrlAnchor, convertToHtmlString: boolean = true, defaultDescription: string = ''): Observable<string> {
    return this.getContextualHelpForCurrentUrl(anchor, convertToHtmlString).pipe(map((help) => (typeof help.description === 'string' ? help.description : defaultDescription)));
  }

  /**
   * Get all user guides and/or videos from contentul, or specify params to narrow search.
   * @param params Customized search params for materials to retrieve:
   * - if content_type is passed in then only a specific content type will be searched for.
   * @returns All help materials from contentful satisfying the params criteria.
   */
  public getUserGuidesAndVideos(params: { searchTerm: string; content_type?: ContentTypes; skipVideos: number; skipUserGuides: number; limit: number }): Observable<MergedContent> {
    if (params && params.content_type) {
      if (params.content_type === ContentTypes.USERGUIDES) {
        return this.getDocumentation(params.skipUserGuides, params.limit, params.searchTerm).pipe(
          map((documentation) => {
            return [undefined, documentation] as MergedContent;
          })
        );
      } else {
        return this.getVideos(params.skipVideos, params.limit, params.searchTerm).pipe(
          map((videos) => {
            return [videos, undefined] as MergedContent;
          })
        );
      }
    }
    return zip(this.getVideos(params.skipVideos, params.limit, params.searchTerm), this.getDocumentation(params.skipUserGuides, params.limit, params.searchTerm)).pipe(
      map(([videos, documentation]: [EntryCollection<ContentFulEntryVideo>, EntryCollection<ContentFulEntryDocument>]) => {
        return [videos, documentation];
      })
    );
  }

  private getVideos(skip: number, limit: number, searchTerm?: string): Observable<EntryCollection<ContentFulEntryVideo>> {
    return from(
      this.client.getEntries<ContentFulEntryVideo>({
        'fields.brand.fields.title': this.brand.companyName,
        'fields.brand.sys.contentType.sys.id': 'brand',
        content_type: ContentTypes.VIDEOS,
        query: searchTerm,
        order: '-sys.updatedAt',
        skip,
        limit,
      })
    );
  }

  private getDocumentation(skip: number, limit: number, searchTerm?: string): Observable<EntryCollection<ContentFulEntryDocument>> {
    return from(
      this.client.getEntries<ContentFulEntryDocument>({
        'fields.brand.fields.title': this.brand.companyName,
        'fields.brand.sys.contentType.sys.id': 'brand',
        content_type: ContentTypes.USERGUIDES,
        query: searchTerm,
        order: '-sys.updatedAt',
        skip,
        limit,
      })
    );
  }
}
